import { useEffect, useMemo, useRef, useState } from 'react';
import { Table, TableBody, TableCell, TableHeader, TableRow } from '@lmig/lmds-react-table';
import { SearchInput } from '@lmig/lmds-react-alphanumeric-input';
import { Select, SelectOption } from '@lmig/lmds-react-select';
import { Pagination, PaginationChangeEvent } from '@lmig/lmds-react-pagination';
import { Notification } from '@lmig/lmds-react-notification';
import { CircularProgressSpinner } from '@lmig/sales-ui-circular-progress-spinner';
import {
  ColumnDef,
  FilterFn,
  SortingState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useUserData } from 'Foundation/User/UserContext';
import { AgencyCodesTooltip } from './AgencyCodesTooltip';
import { buildAgencyCodeRows, getAgencyDetails } from './AgencyCodesApi';
import { IAgencyCodeRow, getStatusSortOrder } from './AgencyCodesModels';
import corporationIcon from './assets/corporation.svg';
import './AgencyCodesPage.scss';

const ROWS_PER_PAGE_CHOICES = [10, 20, 50];
const DEFAULT_ROWS_PER_PAGE = 20;
const USER_DATA_TIMEOUT_MS = 10000;

/** Matches on any column, ignoring dashes so "12345678" finds "12-34-5678". */
const filterAcrossAllColumns: FilterFn<IAgencyCodeRow> = (row, _columnId, filterValue) => {
  const search = String(filterValue ?? '')
    .toLowerCase()
    .trim();
  if (!search) return true;

  const searchWithoutDashes = search.replace(/-/g, '');

  return Object.values(row.original).some((value) => {
    const text = String(value ?? '').toLowerCase();
    return text.includes(search) || text.replace(/-/g, '').includes(searchWithoutDashes);
  });
};

export function AgencyCodesPage() {
  const { userData } = useUserData();

  const [rows, setRows] = useState<IAgencyCodeRow[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [detailsUnavailable, setDetailsUnavailable] = useState<boolean>(false);
  const [sessionUnavailable, setSessionUnavailable] = useState<boolean>(false);
  const [filterValue, setFilterValue] = useState<string>('');
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: DEFAULT_ROWS_PER_PAGE });
  const hasLoadedRef = useRef<boolean>(false);

  useEffect(() => {
    let isActive = true;

    async function loadAgencyCodes() {
      hasLoadedRef.current = true;

      const details = await getAgencyDetails();

      if (!isActive) return;

      setDetailsUnavailable(details === null);
      const builtRows = buildAgencyCodeRows(userData?.agencyCodes, userData?.eCliqCodes, details ?? []);

      setRows(builtRows);
      setIsLoading(false);
    }

    if (userData) loadAgencyCodes();

    return () => {
      isActive = false;
    };
  }, [userData?.agencyCodes, userData?.eCliqCodes]);

  // Stop the spinner if user data never arrives (e.g. session bootstrap failure), and flag it
  // so the empty table isn't mistaken for a user who genuinely has no agency codes.
  useEffect(() => {
    const timeout = window.setTimeout(() => {
      if (!hasLoadedRef.current) {
        setSessionUnavailable(true);
        setIsLoading(false);
      }
    }, USER_DATA_TIMEOUT_MS);

    return () => window.clearTimeout(timeout);
  }, []);

  // Default order is Active, then Transferred, then Terminated, each by agency name A-Z.
  const data = useMemo<IAgencyCodeRow[]>(
    () =>
      [...rows].sort((first, second) => {
        const statusDifference = getStatusSortOrder(first.status) - getStatusSortOrder(second.status);
        if (statusDifference !== 0) return statusDifference;
        return first.agencyName.localeCompare(second.agencyName);
      }),
    [rows],
  );

  const columns = useMemo<ColumnDef<IAgencyCodeRow>[]>(
    () => [
      // Data layer keeps missing values as '' (clean sorting); display shows '--' per design.
      {
        header: 'Agency',
        accessorKey: 'agencyName',
        id: 'agencyName',
        cell: ({ getValue }) => {
          const value = getValue<string>() || '--';
          return (
            <AgencyCodesTooltip text={value}>
              <div>{value}</div>
            </AgencyCodesTooltip>
          );
        },
      },
      { header: 'Code', accessorKey: 'agencyCode', id: 'agencyCode' },
      { header: 'eCLIQ\u00AE', accessorKey: 'eCliqCode', id: 'eCliqCode', cell: ({ getValue }) => (getValue<string>() || '--') },
      { header: 'Status', accessorKey: 'status', id: 'status' },
      {
        header: 'Address',
        accessorKey: 'address',
        id: 'address',
        cell: ({ getValue }) => {
          const value = getValue<string>() || '--';
          return (
            <AgencyCodesTooltip text={value}>
              <div>{value}</div>
            </AgencyCodesTooltip>
          );
        },
      },
      { header: 'City', accessorKey: 'city', id: 'city', cell: ({ getValue }) => (getValue<string>() || '--') },
      { header: 'State', accessorKey: 'state', id: 'state', cell: ({ getValue }) => (getValue<string>() || '--') },
      { header: 'ZIP code', accessorKey: 'zipCode', id: 'zipCode', cell: ({ getValue }) => (getValue<string>() || '--') },
    ],
    [],
  );

  const table = useReactTable({
    data,
    columns,
    state: { sorting, globalFilter: filterValue, pagination },
    globalFilterFn: filterAcrossAllColumns,
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const onFilterChanged = (value: string) => {
    setFilterValue(value);
    setPagination((previous) => ({ ...previous, pageIndex: 0 }));
  };

  const onPaginationChanged = (event: PaginationChangeEvent) => {
    setPagination((previous) =>
      event.itemsPerPage !== previous.pageSize
        ? { pageIndex: 0, pageSize: event.itemsPerPage }
        : { ...previous, pageIndex: event.page },
    );
  };

  const onRowsPerPageChanged = (event: React.FormEvent<HTMLSelectElement>) => {
    const pageSize = Number(event.currentTarget.value);
    if (ROWS_PER_PAGE_CHOICES.includes(pageSize)) setPagination({ pageIndex: 0, pageSize });
  };

  // Subtitle shows the currently selected agency; name comes from our own joined rows.
  const selectedCode = userData?.selectedStatCodeData?.SubStat ?? '';
  const selectedRow = rows.find(
    (row) => row.agencyCode.replace(/-/g, '') === selectedCode.replace(/-/g, ''),
  );
  const subtitle = selectedCode
    ? `${selectedRow?.agencyName ? selectedRow.agencyName + ' ' : ''}(${selectedCode})`
    : '';

  const filteredRowCount = table.getFilteredRowModel().rows.length;
  const visibleRows = table.getRowModel().rows;

  return (
    <div className="agency-codes-page">
      <nav className="agency-codes-breadcrumb" aria-label="Breadcrumb">
        <a href="/start">Homepage</a>
        <span aria-hidden="true">&gt;</span>
        <span aria-current="page">Agency codes</span>
      </nav>
      <div className="agency-codes-page-header">
        <div className="agency-codes-header-title">
          <span className="agency-codes-header-icon">
            <img src={corporationIcon} alt="" aria-hidden="true" />
          </span>
          <h1 className="agency-codes-heading">Agency codes</h1>
        </div>
        {subtitle && <p className="agency-codes-subtitle">{subtitle}</p>}
      </div>

      {sessionUnavailable && (
        <Notification alert="We couldn't load your account information." highlightType="negative">
          Please refresh the page. If the problem continues, contact support.
        </Notification>
      )}

      {detailsUnavailable && (
        <Notification alert="Agency details are temporarily unavailable." highlightType="negative">
          Agency names, statuses and addresses could not be loaded. Your agency codes are still shown below. Please
          try again later.
        </Notification>
      )}

      <div className="agency-codes-page-filter">
        <SearchInput
          id="agency-codes-page-filter-input"
          labelVisual={'Filter by agency, code, address, or eCLIQ\u00AE'}
          value={filterValue}
          aria-live="polite"
          onChange={(e) => onFilterChanged(e.target.value)}
          onClearClicked={() => onFilterChanged('')}
        />
      </div>

      <div className="agency-codes-table-container">
        <div className="agency-codes-table-scroll">
          <Table className="agency-codes-page-table" spacing="compact">
          <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const sortState = header.column.getIsSorted();
                const sortDirection =
                  sortState === 'asc' ? 'ascending' : sortState === 'desc' ? 'descending' : 'none';

                return (
                  <TableCell
                    key={header.id}
                    type="colHead"
                    isSortable={header.column.getCanSort()}
                    sortDirection={sortDirection}
                    onClick={header.column.getCanSort() ? header.column.getToggleSortingHandler() : undefined}
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {isLoading && (
            <TableRow>
              <TableCell colSpan={columns.length} className="agency-codes-message-cell">
                <CircularProgressSpinner size="32" aria-label="Loading agency codes..." />
              </TableCell>
            </TableRow>
          )}
          {!isLoading && visibleRows.length === 0 && (
            <TableRow>
              <TableCell colSpan={columns.length} className="agency-codes-message-cell">
                No matching agency codes
              </TableCell>
            </TableRow>
          )}
            {!isLoading &&
              visibleRows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {cell.column.id === 'status' ? (
                        <span className={`agency-status agency-status-${row.original.status.toLowerCase()}`}>
                          {row.original.status}
                        </span>
                      ) : (
                        flexRender(cell.column.columnDef.cell, cell.getContext())
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
          </TableBody>
          </Table>
        </div>

        <div className="agency-codes-footer">
          {filteredRowCount > pagination.pageSize && (
            <Pagination
              id="agency-codes-pagination"
              className="agency-codes-pagination"
              withWords={false}
              showItemsPerPage={false}
              itemCount={filteredRowCount}
              itemsPerPage={pagination.pageSize}
              page={pagination.pageIndex}
              onChange={onPaginationChanged}
            />
          )}
          {filteredRowCount <= pagination.pageSize && <div className="agency-codes-pagination-placeholder" />}
          <div className="agency-codes-rows-per-page">
            <span className="agency-codes-rows-per-page-label">Items per page</span>
            <Select
              id="agency-codes-rows-per-page"
              labelVisual=""
              labelA11y="Items per page"
              value={String(pagination.pageSize)}
              onChange={onRowsPerPageChanged}
            >
              {ROWS_PER_PAGE_CHOICES.map((choice) => (
                <SelectOption key={choice} value={String(choice)}>
                  {choice}
                </SelectOption>
              ))}
            </Select>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AgencyCodesPage;
